import { CaretDownIcon } from '@phosphor-icons/react';
import React from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import { BRAND_TONE_OPTION_ID, TONE_PRESETS, presetToneInstruction } from '@/lib/tone';
import { QUIET_ACTION } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/**
 * The post inspector's tone control — extracted from PostBody's caption
 * toolbar, where it already sat directly beneath the platform tab strip, and
 * upgraded to fix the three things that made it feel like it never stuck:
 *
 *  1. It offered friendly / professional / casual and NOT the user's own
 *     scanned brand voice, so picking a tone REPLACED the voice onboarding had
 *     just spent a whole step establishing. The brand voice is now the first
 *     and default row.
 *  2. It was fire-and-forget. Nothing was stored, the trigger always read
 *     "Tone", and the next "Shorten" washed the chosen voice back out. The
 *     caller now persists the choice and the trigger reads it back.
 *  3. There was no way home. "Back to brand voice" clears the override.
 *
 * FULLY CONTROLLED, ZERO DATA FETCHING. PostBody stays the owner of the post,
 * of the rewrite call and of the write, so this file cannot grow a second
 * write path that disagrees with the first.
 */

export interface ToneControlProps {
  /** The account's active brand tone, or null when there is none to offer. */
  brandTone: string | null;
  /** `social_posts.tone_override` — null means "inherit the brand voice". */
  value: string | null;
  /** Fires with the preset id and the full rewrite instruction to run. */
  onSelect: (toneId: string, instruction: string) => void;
  /** Clears the override back to the brand voice and rewrites in it. */
  onClearOverride: () => void;
  disabled: boolean;
  /** The preset id currently rewriting, so the row shows its own spinner. */
  pendingToneId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Already resolved by the caller: the tone's name, or its own default. */
  triggerLabel: string;
}

const ROW =
  'flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-skill/10 hover:text-skill';
const ROW_ON = 'bg-sidebar text-foreground';
const ROW_OFF = 'text-foreground';

export const ToneControl: React.FC<ToneControlProps> = ({
  brandTone,
  value,
  onSelect,
  onClearOverride,
  disabled,
  pendingToneId,
  open,
  onOpenChange,
  triggerLabel,
}) => {
  const trimmedBrandTone = brandTone?.trim() ?? '';
  const hasBrandTone = trimmedBrandTone.length > 0;
  const brandRowSelected = value === null;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label="Change caption tone"
          className={cn(QUIET_ACTION, 'h-7 px-1 text-detail font-medium')}
        >
          {pendingToneId ? <Spinner size="sm" className="shrink-0" aria-hidden="true" /> : null}
          {triggerLabel}
          <CaretDownIcon className="size-2.5 shrink-0 opacity-70" weight="bold" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-56 p-1.5">
        <p className="px-2 pb-1 pt-0.5 text-detail font-medium text-muted-foreground/70">Tone</p>
        <div className="flex flex-col">
          {hasBrandTone ? (
            <button
              type="button"
              aria-pressed={brandRowSelected}
              onClick={() => {
                onOpenChange(false);
                onClearOverride();
              }}
              className={cn(ROW, brandRowSelected ? ROW_ON : ROW_OFF)}
            >
              <span className="flex w-full items-center gap-2 text-sm">
                Your brand voice
                {pendingToneId === BRAND_TONE_OPTION_ID ? (
                  <Spinner size="sm" className="ml-auto shrink-0" aria-hidden="true" />
                ) : null}
              </span>
              <span className="line-clamp-2 text-detail text-muted-foreground">
                {trimmedBrandTone}
              </span>
            </button>
          ) : null}
          {TONE_PRESETS.map((preset) => {
            const selected = value === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                aria-pressed={selected}
                onClick={() => {
                  const instruction = presetToneInstruction(preset.id);
                  if (!instruction) return;
                  onOpenChange(false);
                  onSelect(preset.id, instruction);
                }}
                className={cn(ROW, selected ? ROW_ON : ROW_OFF)}
              >
                <span className="flex w-full items-center gap-2 text-sm">
                  {preset.label}
                  {pendingToneId === preset.id ? (
                    <Spinner size="sm" className="ml-auto shrink-0" aria-hidden="true" />
                  ) : null}
                </span>
                <span className="text-detail text-muted-foreground">{preset.blurb}</span>
              </button>
            );
          })}
          {value !== null ? (
            <div className="mt-1 border-t border-border/40 pt-1">
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  onClearOverride();
                }}
                className={cn(
                  ROW,
                  'text-sm font-normal text-muted-foreground/80 hover:text-foreground',
                )}
              >
                {hasBrandTone ? 'Back to brand voice' : 'Clear tone'}
              </button>
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ToneControl;
